import React, { useState, useEffect } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import CKEditorFull from "./CKEditor";
import { EmailTemplateDto } from "../DataService/EmailTemplateService";
import "./EmailTemplateModal.css";

interface Props {
  template?: EmailTemplateDto | null;
  variant?: "mail" | "feelgood" | "public";
  onClose: () => void;
  onSubmit: (dto: {
    name: string;
    subject: string;
    htmlContent: string;
    type: string;
    isActive: boolean;
  }) => Promise<void> | void;
}

export const EmailTemplateModal: React.FC<Props> = ({
  template,
  variant = "mail",
  onSubmit,
  onClose
}) => {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
   const [type, setType] = useState(variant === "public" ? "page-home-hero" : variant === "feelgood" ? "feelgood-both" : "newsletter");
  const [isActive, setIsActive] = useState(true);

  // Pre-fill when editing an existing template
  useEffect(() => {
    if (template) {
      setName(template.name);
      setSubject(template.subject);
      setHtmlContent(template.htmlContent);
      setType(template.type);
      setIsActive(template.isActive);
    }
  }, [template]);

  const handleSave = async () => {
    await onSubmit({
      name,
      subject,
      htmlContent,
      type,
      isActive
    });
  };

  const previewHtml = htmlContent.trim().length > 0
    ? htmlContent
    : '<p>Förhandsvisningen visas här när du skriver innehåll.</p>';

  return (
    <Modal onClose={onClose} className="modal-content-large">
      <div className="email-template-modal">
        <div className="email-template-modal__header">
          <h2 className="email-template-modal__title">{template ? "Redigera mall" : "Skapa ny mall"}</h2>
          <button className="email-template-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="email-template-modal__body">
          <div className="email-template-modal__field">
            <label className="email-template-modal__label">Mall-namn</label>
            <input
              className="email-template-modal__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Varm hälsning"
            />
          </div>

          <div className="email-template-modal__field">
            <label className="email-template-modal__label">Ämne</label>
            <input
              className="email-template-modal__input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Dagens pepp"
            />
          </div>

          <div className="email-template-modal__field">
            <label className="email-template-modal__label">Målgrupp</label>
            <select
              className="email-template-modal__select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {variant === "feelgood" ? (
                <>
                  <option value="feelgood-both">Båda målgrupper</option>
                  <option value="feelgood-member">Medlemmar</option>
                  <option value="feelgood-courseowner">Kursägare</option>
                </>
              ) : variant === "public" ? (
                <>
                  <option value="page-home-hero">Home hero</option>
                  <option value="page-home-rot">Home ROT</option>
                  <option value="page-home-grow">Home GROW</option>
                  <option value="page-about-hero">About hero</option>
                  <option value="page-about-cta">About CTA</option>
                  <option value="page-membership-hero">Membership hero</option>
                  <option value="page-membership-pricing">Membership pricing</option>
                  <option value="page-membership-faq">Membership FAQ</option>
                </>
              ) : (
                <>
                  <option value="newsletter">Nyhetsbrev</option>
                  <option value="campaign">Kampanj</option>
                  <option value="info">Information</option>
                  <option value="goodies">Goodies</option>
                </>
              )}
            </select>
          </div>

          <div className="email-template-modal__richtext">
            <label className="email-template-modal__label">Innehåll</label>
            <CKEditorFull value={htmlContent} onChange={setHtmlContent} />
          </div>

          <div className="email-template-modal__preview">
            <div className="email-template-modal__preview-head">
              <label className="email-template-modal__label">Förhandsvisning</label>
              <span className="email-template-modal__preview-hint">Så ser kortet ut i dashboarden</span>
            </div>
            <div
              className="email-template-modal__preview-body"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>

          <label className="email-template-modal__checkbox">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
            />
            Aktiv mall
          </label>
        </div>

        <div className="email-template-modal__footer">
          <Button
            className="submit-button"
            onClick={handleSave}
            text={template ? "Spara ändringar" : "Skapa mall"}
          />
        </div>
      </div>
    </Modal>
  );
};
